import { useDisclosure } from '@mantine/hooks';
import { debounce } from 'lodash-es';
import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import {
  FilterDropdownItem,
  FilterToolbarGroup,
  FilterToolbarItem,
  SelectedFilters,
} from 'src/components/Filters';
import {
  Content,
  getContent,
  useContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { PosSearchBox } from 'src/core/POS/PosSearchBox';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useComputedState } from 'src/hooks/useComputedState';
import { ChevronDownIcon, ChevronUpIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';

import * as styles from './InventoryListingSortingContent.css';

/**
 * Mobile bottomsheet modal content component for the all selected filters
 */
export const InventoryAllFilterContent = ({
  filters,
  selectedFilters,
  setSelectedFilters,
  onClose,
}: {
  filters: FilterToolbarGroup[];
  selectedFilters: SelectedFilters;
  setSelectedFilters: (_: SelectedFilters) => void;
  onClose: () => void;
}) => {
  const searchPlaceholder = useContent(ContentId.Search);
  const contentContext = useContentContext();

  const [searchText, setSearchText] = useState<string>('');
  const [localSelectedFilters, setLocalSelectedFilters] =
    useComputedState<SelectedFilters>(selectedFilters);

  const searchDeferredValue = useDeferredValue(searchText);

  const filteredFilters = useMemo<FilterToolbarGroup[]>(() => {
    if (!searchDeferredValue) {
      return filters;
    }
    const lowercaseSearchText = searchDeferredValue.toLocaleLowerCase();
    return filters
      .map((filter) => {
        const items = filter.items.filter((filterItem) => {
          const labelValue = getContent(
            filterItem.labelContentId,
            contentContext
          );
          return (
            labelValue.toLocaleLowerCase().indexOf(lowercaseSearchText) !== -1
          );
        });
        return {
          ...filter,
          items,
        };
      })
      .filter((filter) => filter.items.length > 0);
  }, [contentContext, filters, searchDeferredValue]);

  // eslint-disable-next-line react-hooks/rules-of-hooks,react-hooks/exhaustive-deps
  const updateSearchText = useCallback(debounce(setSearchText, 200), []);

  const onAddFilters = useCallback(() => {
    setSelectedFilters(localSelectedFilters);
    onClose();
  }, [localSelectedFilters, onClose, setSelectedFilters]);

  const onResetFilters = useCallback(() => {
    setSelectedFilters(selectedFilters);
  }, [selectedFilters, setSelectedFilters]);

  return (
    <Stack
      direction="column"
      width="full"
      height="full"
      alignItems="center"
      gap="m"
      style={{ color: vars.color.textPrimary }}
    >
      <Stack direction="column" width="full" gap="m">
        <span className={styles.sortAndFilterHeader}>
          <Content id={ContentId.AllFilters} />
        </span>
        <PosSearchBox
          style={{ height: '30px' }}
          placeholder={searchPlaceholder}
          width="100%"
          onSearchChange={updateSearchText}
          rootProps={{ shape: 'square' }}
          focusOnMount={true}
        />
      </Stack>
      <Stack
        direction="column"
        width="full"
        gap="s"
        style={{ flex: 1, overflowY: 'auto' }}
      >
        {filteredFilters.map((filterGroup) => {
          if (filterGroup.items.length === 0 || !filterGroup.titleContentId) {
            return null;
          }
          return (
            <InventoryFilterGroup
              key={filterGroup.titleContentId}
              filterGroup={filterGroup}
              selectedFilters={
                localSelectedFilters[filterGroup.titleContentId] ?? []
              }
              onUpdateSelectedFilters={(updatedFilters) => {
                const updated = {
                  ...localSelectedFilters,
                  [filterGroup.titleContentId as string]: updatedFilters,
                };
                setLocalSelectedFilters(updated);
              }}
            />
          );
        })}
      </Stack>
      <Stack justifyContent="spaceBetween" width="full" alignItems="center">
        <Button
          style={{ height: '40px' }}
          shape="rect"
          variant="outlineGray"
          onClick={onResetFilters}
        >
          <Content id={ContentId.Clear} />
        </Button>
        <Button style={{ height: '40px' }} shape="rect" onClick={onAddFilters}>
          <Content id={ContentId.AddFilter} />
        </Button>
      </Stack>
    </Stack>
  );
};

const InventoryFilterGroup = ({
  filterGroup,
  selectedFilters,
  onUpdateSelectedFilters,
}: {
  filterGroup: FilterToolbarGroup;
  selectedFilters: FilterDropdownItem[];
  onUpdateSelectedFilters: (_: FilterDropdownItem[]) => void;
}) => {
  const [open, { toggle }] = useDisclosure();

  const onToggleFilter = useCallback(
    (item: FilterToolbarItem) => {
      if (selectedFilters.find((filter) => filter.filterId === item.filterId)) {
        onUpdateSelectedFilters(
          selectedFilters.filter((filter) => filter.filterId !== item.filterId)
        );
      } else {
        onUpdateSelectedFilters([
          ...selectedFilters,
          {
            filterId: item.filterId,
            titleContentId: filterGroup.titleContentId,
          },
        ]);
      }
    },
    [filterGroup.titleContentId, onUpdateSelectedFilters, selectedFilters]
  );

  const selectedFilterCount = useMemo(() => {
    const selectedFilterIds = selectedFilters.map((f) => f.filterId);
    return filterGroup.items.filter((item) =>
      selectedFilterIds.includes(item.filterId)
    ).length;
  }, [filterGroup.items, selectedFilters]);

  return (
    <Stack
      width="full"
      direction="column"
      className={styles.inventoryFilterGroupItem}
      style={{ cursor: 'pointer' }}
      gap="l"
    >
      <Stack
        role="button"
        width="full"
        alignItems="center"
        justifyContent="spaceBetween"
        onClick={toggle}
      >
        <Stack alignItems="center" gap="m">
          <span>
            <Content id={filterGroup.titleContentId} />
          </span>
          {selectedFilterCount > 0 && <span>{`(${selectedFilterCount})`}</span>}
        </Stack>
        {open ? (
          <ChevronUpIcon size={vars.iconSize.s} />
        ) : (
          <ChevronDownIcon size={vars.iconSize.s} />
        )}
      </Stack>
      {open && (
        <Stack direction="column" gap="m">
          {filterGroup.items.map((item) => {
            return (
              <Checkbox
                key={item.filterId}
                checked={
                  !!selectedFilters.find(
                    (filter) => filter.filterId === item.filterId
                  )
                }
                onChange={() => onToggleFilter(item)}
                label={<Content id={item.labelContentId} />}
                color={vars.color.textBrand}
                labelPosition="right"
                checkboxSizeVariant="light"
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
